import { useState } from 'react';
import { adminAPI, exportAPI, downloadBlob } from '../../utils/api';
import { useToast, apiError } from '../../components/Toast';
import { TypeToConfirm } from '../../components/BulkActions';
import { Building2, FolderKanban, CheckSquare, Layers, Bug, GitPullRequest,
         GraduationCap, Eraser } from 'lucide-react';

/**
 * Emptying one kind of record in one action.
 *
 * The keys are the backend's own, from services/bulkOps.js. They are also the
 * words typed to confirm, which is deliberate: each one is right for exactly
 * one button on this page, so a confirmation cannot be muscle-memoried from
 * the button beside it. That is why they are not prettified here.
 */
const CLEARABLE = [
  { entity:'clients',           label:'Clients',         Icon:Building2,      note:'Takes their projects, tasks, activities, bugs and change requests' },
  { entity:'projects',          label:'Projects',        Icon:FolderKanban,   note:'Takes their tasks, activities, bugs and change requests' },
  { entity:'tasks',             label:'Tasks',           Icon:CheckSquare,    note:'Takes their activities and bugs' },
  { entity:'activities',        label:'Activities',      Icon:Layers,         note:'Takes the bugs raised against them' },
  { entity:'bugs',              label:'Bugs',            Icon:Bug,            note:'Bugs only' },
  { entity:'change_requests',   label:'Change Requests', Icon:GitPullRequest, note:'Change requests only' },
  { entity:'training_sessions', label:'Training',        Icon:GraduationCap,  note:'Training sessions only' },
];

const nf = (n) => Number(n || 0).toLocaleString();

const PURGE_OPTIONS = [
  { entity:'completed_tasks', label:'Completed Tasks', icon:'✅', desc:'Remove tasks marked as Completed older than N days' },
  { entity:'closed_bugs',     label:'Closed Bugs',     icon:'🐛', desc:'Remove bugs with status Closed older than N days' },
  { entity:'audit_logs',      label:'Audit Logs',      icon:'📋', desc:'Clear audit log entries older than N days' },
  { entity:'notifications',   label:'Notifications',   icon:'🔔', desc:'Remove read notifications older than N days' },
];

export default function AdminDataPage() {
  const toast = useToast();
  const [days, setDays] = useState(90);
  const [purging, setPurging] = useState(null);
  const [results, setResults] = useState({});
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [clearing, setClearing]   = useState(null);   // the entity being previewed
  const [preview, setPreview]     = useState(null);
  const [clearBusy, setClearBusy] = useState(false);

  /** Nothing is offered for confirmation until the server has said what it would do. */
  const askClear = async (opt) => {
    setClearing(opt.entity); setPreview(null);
    try {
      const r = await adminAPI.clearPreview(opt.entity);
      setPreview(r.data);
    } catch (e) {
      toast.error(apiError(e, 'Could not work out what that would clear.'));
      setClearing(null);
    }
  };

  const doClear = async () => {
    setClearBusy(true);
    try {
      const r = await adminAPI.clearEntity(clearing);
      toast.success(r.data?.message || 'Cleared');
      setClearing(null); setPreview(null);
    } catch (e) {
      toast.error(apiError(e, 'Nothing was cleared.'));
    } finally { setClearBusy(false); }
  };



  const handlePurge = async (entity) => {
    setPurging(entity);
    try {
      const r = await adminAPI.purgeData({ entity, older_than_days: days });
      setResults(res => ({...res, [entity]: r.data.deleted ?? r.data.message ?? 'Done'}));
    } catch(e) { toast.error(apiError(e, 'Purge failed. Check server logs.')); } finally { setPurging(null); setConfirmTarget(null); }
  };

  return (
    <div className="space-y-5">
      <div><h1 className="page-title">Data Management</h1><p className="text-sm" style={{ color:'var(--text-secondary)' }}>Manage and clean up system data</p></div>

      {/* Export Section */}
      <div className="card">
        <h3 className="font-semibold mb-4" style={{ color:'var(--text)' }}>Export Data</h3>
        <p className="text-sm mb-4" style={{ color:'var(--text-secondary)' }}>Download complete dataset exports as Excel files.</p>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          {[
            { label:'All Projects',        icon:'📁', fn: () => exportAPI.exportProjects().then(r => downloadBlob(r.data,'projects.xlsx')).catch(e=>toast.error(apiError(e, 'Export failed'))) },
            { label:'All Tasks',           icon:'✅', fn: () => exportAPI.exportTasks().then(r => downloadBlob(r.data,'tasks.xlsx')).catch(e=>toast.error(apiError(e, 'Export failed'))) },
            { label:'All Bugs',            icon:'🐛', fn: () => exportAPI.exportBugs().then(r => downloadBlob(r.data,'bugs.xlsx')).catch(e=>toast.error(apiError(e, 'Export failed'))) },
            { label:'All Change Requests', icon:'🔄', fn: () => exportAPI.exportChangeRequests().then(r => downloadBlob(r.data,'change-requests.xlsx')).catch(e=>toast.error(apiError(e, 'Export failed'))) },
            { label:'All Activities',      icon:'⚡', fn: () => exportAPI.exportActivities().then(r => downloadBlob(r.data,'activities.xlsx')).catch(e=>toast.error(apiError(e, 'Export failed'))) },
            { label:'All Training',        icon:'🎓', fn: () => exportAPI.exportTraining().then(r => downloadBlob(r.data,'training.xlsx')).catch(e=>toast.error(apiError(e, 'Export failed'))) },
          ].map(e => (
            <button key={e.label} onClick={e.fn}
              className="flex items-center gap-3 p-4 rounded-xl border text-left hover:bg-[var(--surface-alt)] transition-colors"
              style={{ borderColor:'var(--border)' }}>
              <span className="text-2xl">{e.icon}</span>
              <div>
                <p className="text-sm font-medium" style={{ color:'var(--text)' }}>{e.label}</p>
                <p className="text-xs" style={{ color:'var(--text-secondary)' }}>Download as .xlsx</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="card">
        <h3 className="font-semibold mb-4" style={{ color:'var(--text)' }}>Purge Old Records</h3>
        <div className="flex items-center gap-3 mb-6 p-4 rounded-xl" style={{ background:'var(--surface-alt)' }}>
          <span className="text-sm font-medium" style={{ color:'var(--text)' }}>Records older than</span>
          <input type="number" className="input w-24 text-sm text-center" value={days} onChange={e=>setDays(parseInt(e.target.value)||30)} min={7} max={3650} />
          <span className="text-sm font-medium" style={{ color:'var(--text)' }}>days will be purged</span>
        </div>
        <div className="space-y-3">
          {PURGE_OPTIONS.map(opt => (
            <div key={opt.entity} className="flex items-center gap-4 p-4 rounded-xl border" style={{ borderColor:'var(--border)', background:'var(--surface)' }}>
              <span className="text-2xl flex-shrink-0">{opt.icon}</span>
              <div className="flex-1">
                <p className="font-medium text-sm" style={{ color:'var(--text)' }}>{opt.label}</p>
                <p className="text-xs" style={{ color:'var(--text-secondary)' }}>{opt.desc}</p>
                {results[opt.entity] !== undefined && (
                  <p className="text-xs mt-1 text-emerald-500">✓ Deleted {results[opt.entity]} records</p>
                )}
              </div>
              <button onClick={() => setConfirmTarget(opt)} disabled={purging === opt.entity}
                className="btn-danger text-sm flex-shrink-0 flex items-center gap-1.5">
                {purging === opt.entity ? <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : '🗑️'} Purge
              </button>
            </div>
          ))}
        </div>
      </div>

      {confirmTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setConfirmTarget(null)} />
          <div className="relative rounded-xl shadow-2xl w-full max-w-sm p-6 z-10" style={{ background:'var(--surface)' }}>
            <h3 className="font-bold mb-2" style={{ color:'var(--text)' }}>Confirm Purge</h3>
            <p className="text-sm mb-1" style={{ color:'var(--text-secondary)' }}>This will permanently delete all <strong>{confirmTarget.label}</strong> older than <strong>{days} days</strong>.</p>
            <p className="text-sm mb-5 text-red-500 font-medium">This action cannot be undone.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirmTarget(null)} className="btn-secondary">Cancel</button>
              <button onClick={() => handlePurge(confirmTarget.entity)} className="btn-danger">Yes, Purge Now</button>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <h3 className="font-semibold mb-1" style={{ color:'var(--text)' }}>Clear a record type</h3>
        <p className="text-sm mb-4" style={{ color:'var(--text-secondary)' }}>
          Empties every record of one kind in one action, together with everything
          underneath it. They move to the recycle bin, so this is reversible: no row
          is removed from the database and any administrator can restore them.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {CLEARABLE.map(opt => (
            <div key={opt.entity}
              className="flex items-center gap-3 p-4 rounded-xl border"
              style={{ borderColor:'var(--border)', background:'var(--surface)' }}>
              <span className="flex-shrink-0" style={{ color:'#2E539F' }}>
                <opt.Icon size={20} />
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm" style={{ color:'var(--text)' }}>{opt.label}</p>
                <p className="text-xs" style={{ color:'var(--text-secondary)' }}>{opt.note}</p>
              </div>
              <button onClick={() => askClear(opt)} disabled={!!clearing}
                className="text-sm px-3 py-1.5 rounded-lg font-medium flex-shrink-0 flex items-center gap-1.5"
                style={{ background:'rgba(255,200,73,.18)', color:'#92700f' }}>
                <Eraser size={14} /> Clear all
              </button>
            </div>
          ))}
        </div>

        <p className="text-xs mt-4" style={{ color:'var(--text-secondary)' }}>
          Removing the rows for good is a separate, deliberate action, in the Recycle Bin.
        </p>
      </div>

      <TypeToConfirm
        open={!!clearing && !!preview}
        word={clearing || ''}
        title={`Clear every ${preview?.label?.toLowerCase() || 'record'}?`}
        body={`Everything of this kind leaves every list and every Deleted view, and lands in the recycle bin. Nothing is removed from the database.`}
        lines={(preview?.affects || []).map(a =>
          `${nf(a.would_move)} ${a.would_move === 1 ? a.label.toLowerCase() : a.plural}` +
          (a.in_bin ? ` (${nf(a.in_bin)} already in the bin, untouched)` : ''))}
        confirmLabel={`Clear ${nf(preview?.would_move)} record${preview?.would_move === 1 ? '' : 's'}`}
        busy={clearBusy}
        onConfirm={doClear}
        onCancel={() => { setClearing(null); setPreview(null); }} />

      <div className="card">
        <h3 className="font-semibold mb-3" style={{ color:'var(--text)' }}>Data Storage Info</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { icon:'🗄️', label:'Database', desc:'PostgreSQL — all structured data' },
            { icon:'📁', label:'File Uploads', desc:'Local disk — /backend/uploads/' },
            { icon:'🔒', label:'Backups', desc:'Configure via your hosting provider' },
          ].map(info => (
            <div key={info.label} className="p-4 rounded-xl" style={{ background:'var(--surface-alt)' }}>
              <p className="text-2xl mb-2">{info.icon}</p>
              <p className="font-semibold text-sm" style={{ color:'var(--text)' }}>{info.label}</p>
              <p className="text-xs mt-1" style={{ color:'var(--text-secondary)' }}>{info.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
